import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight, LuLock, LuInfo, LuChartNoAxesColumn } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import Select from '../components/Select';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, CardConfirm, ListCard, ListCards } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import { PERMISSOES, temPermissao } from '../config/permissions';
import { AtalhoService, type Atalho } from '../services/AtalhoService';
import {
    VARIAVEIS_ATALHO,
    normalizarComando,
    paraBusca,
    partirPorVariaveis,
    variaveisInvalidas,
} from '../lib/atalhos';
import './AtalhosPage.css';

/**
 * Configurações › Atendimento › Atalhos de mensagem (docs/propostas/chat/menu-e-atalhos.html §9).
 *
 * No molde de Tipos de tarefa: ListKit na lista e RecordPanel de dois modos. O que muda é a prévia — num
 * cadastro de texto, o texto É o registro, então a mensagem aparece na linha.
 *
 * Quem tem a tela cria os seus ("Só eu"). O escopo "da empresa" sai com o nome da empresa, então exige
 * `atalhos.gerenciar` (o administrador já a tem): sem ela o seletor fica travado em "Só eu". A API confere
 * de novo — a trava da tela é só cortesia.
 */
const POR_PAGINA = [25, 50, 100];
const LIMITE_CORPO = 1024;
const LIMITE_COMANDO = 40;
const LIMITE_TITULO = 80;
type Modo = 'ver' | 'editar';
type Escopo = 'todos' | 'meus' | 'empresa';
type DonoAtalho = 'eu' | 'empresa';
/** 'padrao' = os meus primeiro, depois a ordem de comando que a API já traz; 'uso' = mais usados nos últimos 30 dias. */
type Ordem = 'padrao' | 'uso';

const vezes = (n: number) => (n === 1 ? '1 vez' : `${n} vezes`);
const usoDe = (a: Pick<Atalho, 'usos30Dias'>) => a.usos30Dias ?? 0;

const rotuloDono = (a: Pick<Atalho, 'daEmpresa'>) => (a.daEmpresa ? 'Empresa' : 'Só eu');

const AtalhosPage = () => {
    const celular = useCelular();
    const podeEmpresa = useMemo(() => temPermissao(PERMISSOES.atalhosGerenciar), []);

    const [atalhos, setAtalhos] = useState<Atalho[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [recorte, gravarRecorte] = useRecorte('at:recorte', {
        busca: '', escopo: 'todos' as Escopo, ordem: 'padrao' as Ordem, pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [escopo, setEscopo] = useState<Escopo>(recorte.escopo);
    const [ordem, setOrdem] = useState<Ordem>(recorte.ordem ?? 'padrao');
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, escopo, ordem, pagina, porPagina });
    }, [busca, escopo, ordem, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<Atalho | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [comando, setComando] = useState('');
    const [titulo, setTitulo] = useState('');
    const [corpo, setCorpo] = useState('');
    const [dono, setDono] = useState<DonoAtalho>('eu');
    const [salvando, setSalvando] = useState(false);
    const [erroServidor, setErroServidor] = useState('');
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    /* A confirmação do "Excluir" do rodapé do painel mora no próprio rodapé: o painel fica aberto até a
       pessoa confirmar, e quem desiste no Cancelar não perde o que estava editando. */
    const [confirmaNoPainel, setConfirmaNoPainel] = useState(false);
    const corpoRef = useRef<HTMLTextAreaElement>(null);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await AtalhoService.getAll();
            setAtalhos(Array.isArray(r.dados) ? r.dados : []);
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a pagina restaurada se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, escopo, ordem, porPagina]);

    const totalMeus = useMemo(() => atalhos.filter(a => !a.daEmpresa).length, [atalhos]);
    const totalEmpresa = atalhos.length - totalMeus;
    const totalUsos = useMemo(() => atalhos.reduce((soma, a) => soma + usoDe(a), 0), [atalhos]);

    const filtrados = useMemo(() => {
        const t = paraBusca(busca.trim());
        const achados = atalhos.filter(a => {
            if (escopo === 'meus' && a.daEmpresa) return false;
            if (escopo === 'empresa' && !a.daEmpresa) return false;
            if (!t) return true;
            return paraBusca(a.comando).includes(t) || paraBusca(a.titulo).includes(t);
        });
        /* A ordenação é estável: no empate de uso ficam a ordem da API (os meus primeiro, depois o comando). */
        return ordem === 'uso' ? [...achados].sort((a, b) => usoDe(b) - usoDe(a)) : achados;
    }, [atalhos, busca, escopo, ordem]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const ehNovo = modo === 'editar' && aberto?.atalhoId === 0;
    /* Atalho da empresa só muda na mão de quem configura o atendimento. */
    const podeMexer = (a: Atalho) => !a.daEmpresa || podeEmpresa;

    const abrirVer = (a: Atalho) => { setConfirmaNoPainel(false); setAberto(a); setModo('ver'); setErroServidor(''); };
    const abrirEdicao = (a: Atalho) => {
        setConfirmaNoPainel(false);
        setAberto(a); setComando(a.comando); setTitulo(a.titulo); setCorpo(a.corpo);
        setDono(a.daEmpresa ? 'empresa' : 'eu'); setModo('editar'); setErroServidor('');
    };
    const abrirNovo = () => {
        setConfirmaNoPainel(false);
        setAberto({ atalhoId: 0, comando: '', titulo: '', corpo: '', ativo: true, daEmpresa: false, usuarioId: null });
        setComando(''); setTitulo(''); setCorpo(''); setDono('eu');
        setModo('editar'); setErroServidor('');
    };
    const fechar = () => { setConfirmaNoPainel(false); setAberto(null); setErroServidor(''); };

    /* ─── Erros mostrados ANTES de salvar ─── */

    const errosCorpo = useMemo(() => variaveisInvalidas(corpo), [corpo]);

    const erroComando = useMemo(() => {
        if (modo !== 'editar') return '';
        if (!comando) return '';
        const daEmpresa = dono === 'empresa';
        const igual = atalhos.find(a =>
            a.comando === comando && a.daEmpresa === daEmpresa && a.atalhoId !== aberto?.atalhoId);
        if (!igual) return '';
        return daEmpresa
            ? 'Já existe um atalho da empresa com este comando. Escolha outro ou edite o que existe.'
            : 'Você já tem um atalho com este comando. Escolha outro ou edite o que existe.';
    }, [modo, comando, dono, atalhos, aberto]);

    /* Meu comando igual ao da empresa não é erro: o meu aparece primeiro, com o rótulo de cada um. */
    const avisoComando = useMemo(() => {
        if (modo !== 'editar' || !comando || dono !== 'eu') return '';
        const daEmpresa = atalhos.some(a => a.daEmpresa && a.comando === comando);
        return daEmpresa
            ? 'A empresa já tem um atalho com este comando. O seu vai aparecer primeiro na lista, com o rótulo "Só eu".'
            : '';
    }, [modo, comando, dono, atalhos]);

    const faltaCampo = !comando || !titulo.trim() || !corpo.trim();
    const temErro = !!erroComando || errosCorpo.length > 0 || corpo.length > LIMITE_CORPO;
    const podeSalvar = !salvando && !faltaCampo && !temErro;

    const inserirVariavel = (chave: string) => {
        const campo = corpoRef.current;
        const trecho = `{{${chave}}}`;
        const ini = campo?.selectionStart ?? corpo.length;
        const fim = campo?.selectionEnd ?? corpo.length;
        const novo = corpo.slice(0, ini) + trecho + corpo.slice(fim);
        setCorpo(novo);
        setErroServidor('');
        requestAnimationFrame(() => {
            campo?.focus();
            const pos = ini + trecho.length;
            campo?.setSelectionRange(pos, pos);
        });
    };

    const corrigirVariavel = (errada: string, certa: string) => {
        setCorpo(c => c.replace(/\{\{\s*([^{}]*?)\s*\}\}/g, (inteiro, nome: string) => (nome === errada ? `{{${certa}}}` : inteiro)));
    };

    const salvar = async () => {
        if (!podeSalvar) return;
        setSalvando(true);
        setErroServidor('');
        try {
            const r = ehNovo
                ? await AtalhoService.create({
                    comando, titulo: titulo.trim(), corpo: corpo.trim(), daEmpresa: dono === 'empresa',
                })
                : await AtalhoService.update({
                    atalhoId: aberto!.atalhoId, comando, titulo: titulo.trim(), corpo: corpo.trim(), ativo: aberto!.ativo,
                });
            if (r.sucesso) {
                aviso.ok(ehNovo ? 'Atalho criado.' : 'Atalho atualizado.');
                await carregar(); fechar();
            } else setErroServidor(r.mensagem || 'Não foi possível salvar o atalho.');
        } catch {
            setErroServidor('Não foi possível salvar o atalho.');
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional pelas duas portas da exclusão: a lixeira da linha e o rodapé do painel. */
    const excluir = async (alvo = aberto) => {
        if (!alvo?.atalhoId) return;
        setSalvando(true);
        try {
            const r = await AtalhoService.delete(alvo.atalhoId);
            if (r.sucesso) { aviso.ok('Atalho excluído.'); await carregar(); fechar(); }
            else aviso.erro(r.mensagem || 'Não foi possível excluir o atalho.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
            setAConfirmar(null);
        }
    };

    const COLUNAS = '1.1fr 1.3fr 3fr 112px 100px 78px';

    const textoDeUso = (a: Atalho) => (usoDe(a) === 0 ? 'não usado' : `usado ${vezes(usoDe(a))}`);

    const perguntaExcluir = (a: Atalho) =>
        a.daEmpresa
            ? `Excluir "/${a.comando}"? Ele some para toda a empresa.`
            : `Excluir "/${a.comando}"?`;

    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {atalhos.length === 0 ? 'Nenhum atalho cadastrado' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {atalhos.length === 0
                    ? 'Quem atende escreve as mesmas frases o dia inteiro. Guarde cada uma com um comando curto e, no Chat, digite / para inseri-la na caixa de mensagem.'
                    : 'Ajuste a busca ou o filtro para ver os outros.'}
            </p>
            {atalhos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo atalho
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    const pilulas = (
        <div className="at-escopos" role="group" aria-label="Filtrar por quem usa">
            {([
                ['todos', 'Todos', atalhos.length],
                ['meus', 'Só meus', totalMeus],
                ['empresa', 'Da empresa', totalEmpresa],
            ] as [Escopo, string, number][]).map(([id, rotulo, n]) => (
                <button key={id} type="button" className={`lk-pill${escopo === id ? ' is-on' : ''}`}
                    aria-pressed={escopo === id} onClick={() => setEscopo(id)}>
                    {rotulo} <b>{n}</b>
                </button>
            ))}
        </div>
    );

    /* Ordem da lista: o desenho de §9 não tinha, e ele é o que revela qual atalho vale manter. */
    const ordens = (
        <div className="at-escopos at-ordem" role="group" aria-label="Ordenar a lista">
            {([
                ['padrao', 'Meus primeiro'],
                ['uso', 'Mais usados'],
            ] as [Ordem, string][]).map(([id, rotulo]) => (
                <button key={id} type="button" className={`lk-pill${ordem === id ? ' is-on' : ''}`}
                    aria-pressed={ordem === id} onClick={() => setOrdem(id)}>
                    {rotulo}
                </button>
            ))}
        </div>
    );

    /* A mensagem com as variáveis em destaque: azul quando existem, vermelho quando não. */
    const textoColorido = (texto: string) => (
        <>
            {partirPorVariaveis(texto).map((p, i) => (
                <span key={i} className={p.tipo === 'texto' ? undefined : p.tipo === 'variavel' ? 'at-var' : 'at-var is-invalida'}>
                    {p.texto}
                </span>
            ))}
        </>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Atalhos de mensagem"
                subtitle={isLoading
                    ? 'Frases prontas para o Chat.'
                    : `${atalhos.length} ${atalhos.length === 1 ? 'atalho' : 'atalhos'} · ${totalMeus} ${totalMeus === 1 ? 'seu' : 'seus'}${totalUsos > 0 ? ` · usados ${vezes(totalUsos)} nos últimos 30 dias` : ''}. No Chat, digite / para inserir.`}
                actions={!isLoading && atalhos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo atalho
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por comando ou título"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    {pilulas}
                    {ordens}
                </div>

                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(a => (
                                    aConfirmar === a.atalhoId ? (
                                        <CardConfirm key={a.atalhoId}
                                            question={perguntaExcluir(a)}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(a)} />
                                    ) : (
                                        <ListCard
                                            key={a.atalhoId}
                                            titulo={`/${a.comando}`}
                                            apoio={`${a.titulo} · ${textoDeUso(a)}`}
                                            estado={rotuloDono(a)}
                                            selected={aberto?.atalhoId === a.atalhoId}
                                            label={`Abrir /${a.comando}`}
                                            onOpen={() => abrirVer(a)}
                                            acoes={podeMexer(a) ? (
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir atalho" danger
                                                    onClick={() => setAConfirmar(a.atalhoId)} />
                                            ) : undefined}
                                        />
                                    )
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                <div className="lk-head">Comando</div>
                                <div className="lk-head">Título</div>
                                <div className="lk-head">Mensagem</div>
                                <div className="lk-head">Uso · 30 dias</div>
                                <div className="lk-head">Quem usa</div>
                                <div className="lk-head lk-head-acts">Ações</div>
                            </div>
                            <div className="lk-body">
                                {isLoading
                                    ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : visiveis.map(a => (
                                        aConfirmar === a.atalhoId ? (
                                            <RowConfirm key={a.atalhoId}
                                                question={perguntaExcluir(a)}
                                                busy={salvando}
                                                onCancel={() => setAConfirmar(null)}
                                                onConfirm={() => excluir(a)} />
                                        ) : (
                                            <ListRow key={a.atalhoId} columns={COLUNAS}
                                                selected={aberto?.atalhoId === a.atalhoId}>
                                                <span className="lk-cell lk-strong"><span>/{a.comando}</span></span>
                                                <span className="lk-cell"><span>{a.titulo}</span></span>
                                                <span className="lk-cell lk-muted"><span>{a.corpo}</span></span>
                                                <span className={`lk-cell${usoDe(a) === 0 ? ' lk-muted' : ''}`}>
                                                    <span>{textoDeUso(a)}</span>
                                                </span>
                                                <span className="lk-cell">
                                                    <span className="lk-tag">{rotuloDono(a)}</span>
                                                </span>
                                                <RowActions>
                                                    <RowAction icon={<LuEye size={14} />} label="Ver atalho"
                                                        onClick={() => abrirVer(a)} />
                                                    {podeMexer(a) && (
                                                        <RowAction icon={<LuTrash2 size={14} />} label="Excluir atalho" danger
                                                            onClick={() => setAConfirmar(a.atalhoId)} />
                                                    )}
                                                </RowActions>
                                            </ListRow>
                                        )
                                    ))}
                            </div>
                        </div>

                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                title={modo === 'editar'
                    ? (comando ? `/${comando}` : (ehNovo ? 'Novo atalho' : 'Atalho'))
                    : (aberto ? `/${aberto.comando}` : '')}
                subtitle={aberto && !ehNovo ? `${aberto.titulo} · ${rotuloDono(aberto)}` : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && !ehNovo && podeMexer(aberto) && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={modo === 'editar' && confirmaNoPainel && aberto ? (
                    <>
                        <span className="at-confirma">{perguntaExcluir(aberto)}</span>
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => setConfirmaNoPainel(false)}>Cancelar</button>
                        <button type="button" className="rp-btn is-danger" disabled={salvando}
                            onClick={() => excluir()}>
                            {salvando ? 'Excluindo…' : 'Excluir'}
                        </button>
                    </>
                ) : modo === 'editar' ? (
                    <>
                        {!ehNovo && aberto && podeMexer(aberto) && (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmaNoPainel(true)} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={!podeSalvar}>
                            {salvando ? 'Salvando…' : 'Salvar atalho'}
                        </button>
                    </>
                ) : undefined}
            >
                {modo === 'ver' && aberto && (
                    <>
                        <PanelSection title="Mensagem">
                            <p className="at-texto">{textoColorido(aberto.corpo)}</p>
                            <p className="at-dica">
                                As variáveis em azul são trocadas pelos dados da conversa na hora de inserir.
                            </p>
                        </PanelSection>
                        <PanelSection title="Quem usa">
                            <p className="at-texto">
                                {aberto.daEmpresa ? 'Toda a empresa.' : 'Só você.'}
                            </p>
                            {aberto.daEmpresa && !podeEmpresa && (
                                <p className="at-dica at-dica-icone">
                                    <LuLock size={12} /> Só quem configura o atendimento edita os atalhos da empresa.
                                </p>
                            )}
                        </PanelSection>
                        {/* Conta inserção, não envio: o que interessa é se o atalho serviu. */}
                        <div className="at-uso">
                            <LuChartNoAxesColumn size={13} />
                            <span>
                                {usoDe(aberto) === 0
                                    ? 'Ainda não foi usado nos últimos 30 dias'
                                    : `Usado ${vezes(usoDe(aberto))} nos últimos 30 dias · por ${aberto.pessoasUsaram30Dias ?? 0} ${(aberto.pessoasUsaram30Dias ?? 0) === 1 ? 'pessoa' : 'pessoas'}`}
                            </span>
                        </div>
                    </>
                )}

                {modo === 'editar' && (
                    <>
                        <PanelSection title="Atalho">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="at-comando">Comando</label>
                                <div className={`at-comando${erroComando ? ' has-error' : ''}`}>
                                    <span className="at-barra" aria-hidden="true">/</span>
                                    <input id="at-comando" className="at-comando-in" type="text" autoFocus
                                        value={comando} maxLength={LIMITE_COMANDO} placeholder="orcamento"
                                        onChange={e => { setComando(normalizarComando(e.target.value)); setErroServidor(''); }} />
                                </div>
                                {erroComando
                                    ? <span className="rp-error">{erroComando}</span>
                                    : avisoComando
                                        ? <span className="at-dica">{avisoComando}</span>
                                        : <span className="at-dica">
                                            Sem espaço e sem acento. É o que se digita no Chat depois da barra.
                                        </span>}
                            </div>
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="at-titulo">Título</label>
                                <input id="at-titulo" className="rp-input" type="text"
                                    value={titulo} maxLength={LIMITE_TITULO} placeholder="Como você reconhece este atalho"
                                    onChange={e => { setTitulo(e.target.value); setErroServidor(''); }} />
                                <span className="at-dica">Aparece na lista do "/" e também é buscável.</span>
                            </div>
                        </PanelSection>

                        <PanelSection title="Mensagem">
                            <div className="rp-field">
                                <textarea id="at-corpo" ref={corpoRef}
                                    className={`rp-input is-area${errosCorpo.length ? ' has-error' : ''}`}
                                    rows={5} value={corpo} aria-label="Mensagem"
                                    placeholder="Olá {{primeiro_nome}}, tudo bem?"
                                    onChange={e => { setCorpo(e.target.value); setErroServidor(''); }} />
                                <div className="at-contagem" data-estouro={corpo.length > LIMITE_CORPO || undefined}>
                                    {corpo.length}/{LIMITE_CORPO}
                                </div>
                                {errosCorpo.map(v => (
                                    <span key={v.nome} className="rp-error at-erro-var">
                                        A variável <b className="at-var is-invalida">{`{{${v.nome}}}`}</b> não existe.
                                        {v.sugestao && (
                                            <>
                                                {' '}Você quis dizer{' '}
                                                <button type="button" className="at-corrige"
                                                    onClick={() => corrigirVariavel(v.nome, v.sugestao!)}>
                                                    {`{{${v.sugestao}}}`}
                                                </button>?
                                            </>
                                        )}
                                    </span>
                                ))}
                            </div>
                            <div className="at-vars" role="group" aria-label="Inserir variável">
                                {VARIAVEIS_ATALHO.map(v => (
                                    <button key={v.chave} type="button" className="at-chip" title={v.descricao}
                                        onClick={() => inserirVariavel(v.chave)}>
                                        {`{{${v.chave}}}`}
                                    </button>
                                ))}
                            </div>
                            <span className="at-dica">Clique na variável para inserir no ponto do cursor.</span>
                            {corpo.includes('{{') && (
                                <div className="at-previa" aria-label="Prévia com as variáveis destacadas">
                                    {textoColorido(corpo)}
                                </div>
                            )}
                            <span className="at-dica at-dica-icone">
                                <LuInfo size={12} />
                                <span>
                                    O atalho é texto livre e não reabre a janela de 24 horas da Meta: com a janela
                                    fechada o Chat continua travado. Para reativar uma conversa, use um template aprovado.
                                </span>
                            </span>
                        </PanelSection>

                        <PanelSection title="Quem usa">
                            <div className="rp-field">
                                <Select
                                    value={dono}
                                    onChange={v => setDono(v as DonoAtalho)}
                                    disabled={!ehNovo || !podeEmpresa}
                                    portal
                                    options={[
                                        { value: 'eu', label: 'Só eu' },
                                        { value: 'empresa', label: 'Toda a empresa' },
                                    ]}
                                />
                                <span className="at-dica at-dica-icone">
                                    {!podeEmpresa
                                        ? <><LuLock size={12} /> Só quem configura o atendimento cria atalhos da empresa.</>
                                        : !ehNovo
                                            ? 'Para mudar quem usa, crie um novo atalho.'
                                            : dono === 'empresa'
                                                ? 'Aparece para todo mundo, em nome da empresa.'
                                                : 'Só você vê e usa este atalho.'}
                                </span>
                            </div>
                        </PanelSection>

                        {erroServidor && <p className="rp-error at-erro-servidor">{erroServidor}</p>}
                    </>
                )}
            </RecordPanel>
        </div>
    );
};

export default AtalhosPage;
